import React, { useState } from 'react';

import { Box, FormControlLabel, Switch, Typography } from '@mui/material';
import CurrencyFormatter from '@apigrowfinance/pbolt-currency-formatter';
import Icon from '@apigrowfinance/pbolt-icon';
import RowItem from '@apigrowfinance/pbolt-row-item';
import Tag from '@apigrowfinance/pbolt-tag';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import {
    RiBankCardLine,
    RiEqualizerLine,
    RiAccountCircleLine,
    RiErrorWarningLine,
} from 'react-icons/ri';
import { FormattedMessage, FormattedPlural, FormattedNumber } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import {
    CustomerFlexControl,
    FlexControl,
} from '@clients/domains/flexControls/flexControlsClient';
import { useAccount } from '@contexts/AccountProvider';
import usePatchUpdateAccountFlexControl from '@hooks/queries/usePatchUpdateAccountFlexControl';
import usePatchUpdateCustomerFlexControl from '@hooks/queries/usePatchUpdateCustomerFlexControl';
import flexControls from '@messages/flexControls/flexControls';
import getEnumTranslation from '@utils/getEnumTranslation';

interface FlexControlItemProps {
    flexControl: FlexControl | CustomerFlexControl;
    selectedCustomerId: number;
}

const FlexControlItem = ({
    flexControl,
    selectedCustomerId,
}: FlexControlItemProps) => {
    const navigate = useNavigate();
    const [isSwitchChecked, setIsSwitchChecked] = useState(flexControl?.active);

    const {
        account: { account_id },
        program,
    } = useAccount();
    const { theme } = useTheme();
    const customerFlexControl = flexControl as CustomerFlexControl;

    const { mutate: mutateAccount } = usePatchUpdateAccountFlexControl({
        accountId: account_id,
        flexControlId: flexControl?.id,
        onError: () => {
            // revert optimistic update
            setIsSwitchChecked(!isSwitchChecked);
        },
    });

    const { mutate: mutateCustomer } = usePatchUpdateCustomerFlexControl({
        accountId: account_id,
        customerId: selectedCustomerId,
        flexControlId: flexControl?.id,
        onError: () => {
            // revert optimistic update
            setIsSwitchChecked(!isSwitchChecked);
        },
    });

    const disabledClick = (event: React.SyntheticEvent) => {
        event.stopPropagation();
        const optimisticUpdate = !isSwitchChecked;
        setIsSwitchChecked(optimisticUpdate);
        if (selectedCustomerId) {
            mutateCustomer({
                active: optimisticUpdate,
            } as CustomerFlexControl);
        } else {
            mutateAccount({
                active: optimisticUpdate,
            } as FlexControl);
        }
    };

    const viewFlexControlDetails = () => {
        if (selectedCustomerId) {
            navigate(
                `customer/${selectedCustomerId}/flexcontrol/${flexControl?.id}`,
            );
        } else {
            navigate(`flexcontrol/${flexControl?.id}`);
        }
    };

    const getChipLabel = () => {
        if (customerFlexControl?.card_ids?.length) {
            return (
                <FormattedPlural
                    value={customerFlexControl?.card_ids?.length}
                    one={getEnumTranslation('card')}
                    other={getEnumTranslation('cards')}
                />
            );
        } else {
            return getEnumTranslation('customer');
        }
    };

    const SpacedDot = () => {
        return <span style={{ marginLeft: 5, marginRight: 5 }}>•</span>;
    };

    const getDuration = (dot = false) => {
        switch (flexControl?.limit_duration) {
            case 'P1D':
                return (
                    <>
                        {dot && <SpacedDot />}
                        <span>
                            {getEnumTranslation('daily', {
                                messages: flexControls,
                            })}
                        </span>
                    </>
                );
            case 'P1W':
                return (
                    <>
                        {dot && <SpacedDot />}
                        <span>
                            {getEnumTranslation('weekly', {
                                messages: flexControls,
                            })}
                        </span>
                    </>
                );
            case 'P1M':
                return (
                    <>
                        {dot && <SpacedDot />}
                        <span>
                            {getEnumTranslation('monthly', {
                                messages: flexControls,
                            })}
                        </span>
                    </>
                );
            default:
                return '';
        }
    };

    const getLimit = () => {
        if (flexControl?.available_limit && flexControl?.max_limit) {
            if (flexControl?.available_limit === 0) {
                return (
                    <>
                        <SpacedDot />
                        {flexControl?.type === 'spending_limit' ? (
                            <CurrencyFormatter
                                currency={program?.currency_numeric_code}
                                amount={flexControl?.max_limit}
                                fontWeight={300}
                            />
                        ) : (
                            <FormattedNumber value={flexControl?.max_limit} />
                        )}
                        <FormattedMessage id="used" defaultMessage="used" />
                    </>
                );
            } else {
                return (
                    <>
                        <SpacedDot />
                        {flexControl?.type === 'spending_limit' ? (
                            <>
                                <span
                                    data-testid={`used_limit_${
                                        flexControl?.max_limit -
                                        flexControl?.available_limit
                                    }`}>
                                    <CurrencyFormatter
                                        currency={
                                            program?.currency_numeric_code
                                        }
                                        amount={
                                            flexControl?.max_limit -
                                            flexControl?.available_limit
                                        }
                                        fontWeight={300}
                                    />
                                </span>
                                <span>/</span>
                                <span
                                    data-testid={`max_limit_${flexControl?.max_limit}`}>
                                    <CurrencyFormatter
                                        currency={
                                            program?.currency_numeric_code
                                        }
                                        amount={flexControl?.max_limit}
                                        fontWeight={300}
                                    />
                                </span>
                            </>
                        ) : (
                            <>
                                <span
                                    data-testid={`used_limit_${
                                        flexControl?.max_limit -
                                        flexControl?.available_limit
                                    }`}>
                                    <FormattedNumber
                                        value={
                                            flexControl?.max_limit -
                                            flexControl?.available_limit
                                        }
                                    />
                                </span>
                                <span>/</span>
                                <span
                                    data-testid={`max_limit_${flexControl?.max_limit}`}>
                                    <FormattedNumber
                                        value={flexControl?.max_limit}
                                    />
                                </span>
                            </>
                        )}
                        <span style={{ marginLeft: 3 }}>
                            <FormattedMessage id="used" defaultMessage="used" />
                        </span>
                    </>
                );
            }
        } else {
            return '';
        }
    };

    const getSubtitle = () => {
        return (
            <Typography variant="body2" fontWeight="normal">
                <span data-testid={`${flexControl?.id}-${flexControl?.type}`}>
                    {getEnumTranslation(flexControl?.type, {
                        messages: flexControls,
                    })}
                </span>
                {getDuration(true)}
                {getLimit()}
            </Typography>
        );
    };

    const getLimitReachedLabel = () => {
        return (
            <Typography variant="body2" fontWeight="normal">
                {getDuration()}
                <span style={{ marginLeft: 3 }}>
                    <FormattedMessage
                        id="FlexControlItem.limitReached"
                        defaultMessage="limit reached"
                    />
                </span>
            </Typography>
        );
    };

    return (
        <RowItem
            key={flexControl?.id}
            onClick={viewFlexControlDetails}
            primaryTitle={flexControl?.name}
            primarySubtitle={getSubtitle()}
            secondaryTitle={
                <Box>
                    {flexControl?.available_limit === 0 && (
                        <Tag
                            label={getLimitReachedLabel()}
                            icon={<RiErrorWarningLine color="error.dark" />}
                            color="default"
                        />
                    )}
                    {!!selectedCustomerId && (
                        <Tag
                            label={getChipLabel()}
                            icon={
                                customerFlexControl?.card_ids?.length ? (
                                    <RiBankCardLine />
                                ) : (
                                    <RiAccountCircleLine />
                                )
                            }
                            color="default"
                        />
                    )}
                    <FormControlLabel
                        sx={{
                            color: isSwitchChecked
                                ? theme?.palette?.primary?.main
                                : theme?.palette?.text?.primary,
                        }}
                        control={
                            <Switch
                                onClick={disabledClick}
                                checked={isSwitchChecked}
                                data-testid="flex-control-switch"
                            />
                        }
                        label={
                            <Typography variant="overline">
                                {getEnumTranslation(
                                    isSwitchChecked ? 'enabled' : 'disabled',
                                )}
                            </Typography>
                        }
                    />
                </Box>
            }
            avatar={
                <Icon>
                    <RiEqualizerLine />
                </Icon>
            }
        />
    );
};

export default FlexControlItem;
